import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, Users, Award, ArrowRight, CheckCircle } from 'lucide-react';
import { STATS } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const STATS_KEYS = ['stats.courses', 'stats.levels', 'stats.portal', 'stats.online'];

export default function About() {
  const { t } = useSiteLanguage();

  const VALUES = [
    { icon: Target, titleKey: 'about.values.accessibility.title', descKey: 'about.values.accessibility.desc' },
    { icon: Award,  titleKey: 'about.values.quality.title',       descKey: 'about.values.quality.desc' },
    { icon: Heart,  titleKey: 'about.values.learner.title',        descKey: 'about.values.learner.desc' },
    { icon: Users,  titleKey: 'about.values.community.title',      descKey: 'about.values.community.desc' },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="About Us"
        description="Atlas Academy delivers flexible, accredited online qualifications across the UK in partnership with Ofqual-regulated awarding bodies OTHM and QUALIFI."
        path="/about"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('about.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-5xl font-extrabold mb-4">{t('about.hero.title')}</h1>
          <p className="text-blue-100/70 max-w-2xl text-lg leading-relaxed">
            {t('about.hero.desc')}
          </p>
        </div>
      </div>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('about.mission.eyebrow')}</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-5">
                {t('about.mission.title')}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                {t('about.mission.para1')}
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('about.mission.para2')}
              </p>
              <div className="space-y-2">
                {[
                  t('about.mission.point1'),
                  t('about.mission.point2'),
                  t('about.mission.point3'),
                  t('about.mission.point4'),
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={16} className="text-blue-600 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 lg:h-auto">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop&q=80"
                alt="Our team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('about.values.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('about.values.title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(({ icon: Icon, titleKey, descKey }) => (
              <div key={titleKey} className="bg-white rounded-xl p-6 border border-gray-100 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={22} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{t(titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-blue-700 py-12">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {STATS.map((stat, i) => (
            <div key={i}>
              <p className="text-3xl lg:text-4xl font-extrabold text-white">{stat.value}</p>
              <p className="text-blue-200 text-sm mt-1">{t(STATS_KEYS[i], stat.label)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm font-medium uppercase tracking-wide mb-6">{t('about.accreditations.label')}</p>
          <div className="flex items-center justify-center gap-10 flex-wrap">
            <div className="w-44 h-24 bg-[#0f2b4a] rounded-xl shadow-md p-3 flex items-center justify-center hover:shadow-lg transition-shadow">
              <img src="/images/othm-logo.svg" alt="OTHM Awarding Body" className="w-full h-auto max-h-16 object-contain" />
            </div>
            <div className="w-44 h-24 bg-white border border-gray-200 rounded-xl shadow-md p-1 flex items-center justify-center hover:shadow-lg transition-shadow">
              <img src="/images/qualifi-logo.png" alt="Qualifi Awarding Body" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f2b4a] py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">{t('about.cta.title')}</h2>
          <p className="text-blue-200/70 mb-7">{t('about.cta.desc')}</p>
          <Link to="/courses" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors">
            {t('about.cta.btn')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
